import { z } from 'zod';
import {
  CadenceSchema,
  ReportEditorDto,
  ReportStatusSchema,
  SectionIdSchema,
  SectionInsightsSchema,
} from './reportEnums.constants';

/** Report instance response DTO. */
export interface ReportDto {
  id: string;
  name: string;
  customerId: string;
  cadence: z.infer<typeof CadenceSchema>;
  reportingPeriod: string;
  /** Warehouse measurement period for JOC / quality measure sections. */
  measurementPeriod: string | null;
  status: z.infer<typeof ReportStatusSchema>;
  createdFromTemplateId: string | null;
  createdAt: string;
  updatedAt: string;
  lastEditedBy: ReportEditorDto | null;
}

/** Report with nested section rows. */
export interface ReportDetailDto extends ReportDto {
  sections: ReportSectionDto[];
}

/** Report section instance response DTO. */
export interface ReportSectionDto {
  id: string;
  reportId: string;
  sectionId: z.infer<typeof SectionIdSchema>;
  order: number;
  included: boolean;
  calloutEnabled: boolean;
  includedInSlides: boolean;
  narrative: string | null;
  callout: string | null;
  calloutTitle: string | null;
  clinicalInsightEnabled: boolean;
  financialInsightEnabled: boolean;
  insights: z.infer<typeof SectionInsightsSchema> | null;
  dataSnapshot: Record<string, unknown> | null;
  updatedAt: string;
}

/** Request body for creating a report. */
export const CreateReportRequestSchema = z
  .object({
    name: z.string().min(1),
    customerId: z.string().uuid(),
    cadence: CadenceSchema,
    reportingPeriod: z.string().min(1),
    measurementPeriod: z.string().min(1).nullable().optional(),
    createdFromTemplateId: z.string().uuid().optional(),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.cadence === 'adhoc' && !data.measurementPeriod?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'measurementPeriod is required for JOC reports',
        path: ['measurementPeriod'],
      });
    }
  });

/** Parsed create-report request. */
export type CreateReportRequestDto = z.infer<typeof CreateReportRequestSchema>;

/** Request body for updating report metadata. */
export const UpdateReportRequestSchema = z
  .object({
    name: z.string().min(1).optional(),
    reportingPeriod: z.string().min(1).optional(),
    measurementPeriod: z.string().min(1).nullable().optional(),
  })
  .strict();

/** Parsed update-report request. */
export type UpdateReportRequestDto = z.infer<typeof UpdateReportRequestSchema>;

/** Request body for patching a single report section row. */
export const UpdateReportSectionRequestSchema = z
  .object({
    included: z.boolean().optional(),
    calloutEnabled: z.boolean().optional(),
    includedInSlides: z.boolean().optional(),
    narrative: z.string().nullable().optional(),
    callout: z.string().nullable().optional(),
    calloutTitle: z.string().nullable().optional(),
    clinicalInsightEnabled: z.boolean().optional(),
    financialInsightEnabled: z.boolean().optional(),
    insights: SectionInsightsSchema.nullable().optional(),
  })
  .strict();

/** Parsed patch-section request. */
export type UpdateReportSectionRequestDto = z.infer<
  typeof UpdateReportSectionRequestSchema
>;

/** One entry when reordering report sections. */
export const ReorderReportSectionEntrySchema = z.object({
  sectionRowId: z.string().uuid(),
  order: z.number().int().min(1),
});

/** Request body for PUT section reorder. */
export const ReorderReportSectionsRequestSchema = z
  .object({
    sections: z.array(ReorderReportSectionEntrySchema).min(1),
  })
  .strict();

/** Parsed reorder-sections request. */
export type ReorderReportSectionsRequestDto = z.infer<
  typeof ReorderReportSectionsRequestSchema
>;

/** Section library list response. */
export interface SectionLibraryResponseDto {
  sections: import('./reportEnums.constants').SectionMetaDto[];
}

/** Outcome of refreshing one section's warehouse snapshot. */
export type ReportSectionSnapshotRefreshStatus =
  | 'updated'
  | 'cleared'
  | 'failed'
  | 'unchanged'
  | 'skipped';

/** Per-section result from POST refresh-snapshots. */
export interface ReportSectionSnapshotRefreshResultDto {
  sectionRowId: string;
  sectionId: z.infer<typeof SectionIdSchema>;
  status: ReportSectionSnapshotRefreshStatus;
  /** Machine-readable detail when status is `failed`. */
  reason?: string;
}

/** Response body for POST /reports/:id/refresh-snapshots. */
export interface RefreshReportSnapshotsResponseDto {
  report: ReportDetailDto;
  sections: ReportSectionSnapshotRefreshResultDto[];
  hadFailures: boolean;
}
